import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
} from '@mui/material';

interface ConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  isLoading?: boolean;
  loadingText?: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'danger' | 'warning' | 'info' | 'success';
  icon?: string;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  isLoading = false,
  loadingText = "Đang xử lý...",
  confirmText = "Xác nhận",
  cancelText = "Hủy",
  type = 'danger',
  icon,
}) => {
  const getTypeStyles = () => {
    switch (type) {
      case 'danger':
        return {
          iconBg: '#fef2f2',
          iconColor: '#dc2626',
          warningBg: '#fef2f2',
          warningBorder: '#fecaca',
          warningColor: '#dc2626',
          buttonBg: '#dc2626',
          buttonHover: '#b91c1c',
          buttonShadow: 'rgba(220, 38, 38, 0.3)',
          defaultIcon: '🗑️'
        };
      case 'warning':
        return {
          iconBg: '#fffbeb',
          iconColor: '#d97706',
          warningBg: '#fffbeb',
          warningBorder: '#fed7aa',
          warningColor: '#d97706',
          buttonBg: '#d97706',
          buttonHover: '#b45309',
          buttonShadow: 'rgba(217, 119, 6, 0.3)',
          defaultIcon: '⚠️'
        };
      case 'info':
        return {
          iconBg: '#eff6ff',
          iconColor: '#2563eb',
          warningBg: '#eff6ff',
          warningBorder: '#bfdbfe',
          warningColor: '#2563eb',
          buttonBg: '#2563eb',
          buttonHover: '#1d4ed8',
          buttonShadow: 'rgba(37, 99, 235, 0.3)',
          defaultIcon: 'ℹ️'
        };
      case 'success':
        return {
          iconBg: '#f0fdf4',
          iconColor: '#16a34a',
          warningBg: '#f0fdf4',
          warningBorder: '#bbf7d0',
          warningColor: '#16a34a',
          buttonBg: '#16a34a',
          buttonHover: '#15803d',
          buttonShadow: 'rgba(22, 163, 74, 0.3)',
          defaultIcon: '✅'
        };
      default:
        return {
          iconBg: '#fef2f2',
          iconColor: '#dc2626',
          warningBg: '#fef2f2',
          warningBorder: '#fecaca',
          warningColor: '#dc2626',
          buttonBg: '#dc2626',
          buttonHover: '#b91c1c',
          buttonShadow: 'rgba(220, 38, 38, 0.3)',
          defaultIcon: '🗑️'
        };
    }
  };

  const styles = getTypeStyles();
  const displayIcon = icon || styles.defaultIcon;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="confirmation-dialog-title"
      aria-describedby="confirmation-dialog-description"
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        }
      }}
    >
      <DialogTitle 
        id="confirmation-dialog-title"
        sx={{
          pb: 1,
          fontSize: '1.25rem',
          fontWeight: 600,
          color: '#1f2937',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            backgroundColor: styles.iconBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: styles.iconColor
          }}
        >
          {displayIcon}
        </Box>
        {title}
      </DialogTitle>
      
      <DialogContent sx={{ pt: 2, pb: 3 }}>
        <DialogContentText 
          id="confirmation-dialog-description"
          sx={{
            fontSize: '1rem',
            color: '#374151',
            lineHeight: 1.6,
            mb: type === 'danger' ? 2 : 0
          }}
        >
          {message}
        </DialogContentText>
        
        {type === 'danger' && (
          <Box
            sx={{
              backgroundColor: styles.warningBg,
              border: `1px solid ${styles.warningBorder}`,
              borderRadius: 2,
              p: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                backgroundColor: styles.warningColor + '80',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: styles.warningColor,
                fontSize: '0.75rem'
              }}
            >
              ⚠️
            </Box>
            <Box sx={{ fontSize: '0.875rem', color: styles.warningColor, fontWeight: 500 }}>
              Hành động này không thể hoàn tác!
            </Box>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 3, pt: 0, gap: 1 }}>
        <Button 
          onClick={onClose} 
          disabled={isLoading}
          variant="outlined"
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 500,
            px: 3,
            py: 1,
            borderColor: '#d1d5db',
            color: '#374151',
            '&:hover': {
              borderColor: '#9ca3af',
              backgroundColor: '#f9fafb'
            }
          }}
        >
          {cancelText}
        </Button>
        
        <Button 
          onClick={onConfirm} 
          disabled={isLoading}
          variant="contained"
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            py: 1,
            backgroundColor: styles.buttonBg,
            boxShadow: `0 1px 3px 0 ${styles.buttonShadow}`,
            '&:hover': {
              backgroundColor: styles.buttonHover,
              boxShadow: `0 4px 6px -1px ${styles.buttonShadow}`
            },
            '&:disabled': {
              backgroundColor: styles.buttonBg + '80',
              color: '#ffffff'
            }
          }}
          autoFocus
        >
          {isLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  border: '2px solid #ffffff',
                  borderTop: '2px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' }
                  }
                }}
              />
              {loadingText}
            </Box>
          ) : (
            confirmText
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmationModal;

